import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Search, Phone, Mail, MapPin, DollarSign, User,
  Check, AlertCircle, Calendar, FileText, ChevronLeft, ChevronRight,
  MessageSquare, ArrowRight, TrendingUp, Target, Clock, 
  Users as UsersIcon, Building, Briefcase, Tag, ArrowUpRight,
  Sparkles, CheckCircle2, XCircle, Loader2, PhoneCall, 
  ClipboardList, Wrench, Truck, Settings, History,
  Ruler, Calculator, Image, Package, CreditCard, Paintbrush
} from 'lucide-react';
import { ProjectDetailsTab } from '../components/projects/ProjectDetailsTab';
import { ScopeOfWorkTab } from '../components/projects/ScopeOfWorkTab';
import { MeasurementsTab } from '../components/projects/MeasurementsTab';
import { AccountingSection } from '../components/projects/AccountingSection';
import { WorkOrdersTab } from '../components/projects/WorkOrdersTab';
import { JobCommunicationsTab } from '../components/jobs/JobCommunicationsTab';
import { EstimatesTab } from '../components/estimates/EstimatesTab';
import { FilesTab } from '../components/files/FilesTab';
import { CreateWorkOrderModal } from '../components/work-orders/CreateWorkOrderModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role gate for Work Order creation
const WO_CREATE_ROLES = ['project_manager', 'operations_manager', 'admin', 'super_admin'];

// Project Tabs Configuration - CANONICAL ORDER (do not reorder)
const PROJECT_TABS = [
  { id: 'overview', label: 'Overview', icon: FileText, roles: ['all'], onboardId: null },
  { id: 'details', label: 'Details', icon: ClipboardList, roles: ['all'], onboardId: null },
  { id: 'communications', label: 'Communications', icon: MessageSquare, roles: ['all'], onboardId: null },
  { id: 'scope', label: 'Scope of Work', icon: Paintbrush, roles: ['sales', 'ops', 'admin', 'super_admin', 'director_of_sales', 'ops_manager', 'project_manager', 'foreman'], onboardId: null },
  { id: 'measurements', label: 'Measurements', icon: Ruler, roles: ['sales', 'ops', 'admin', 'super_admin', 'director_of_sales'], onboardId: 'measurements-tab' },
  { id: 'estimates', label: 'Estimates', icon: Calculator, roles: ['sales', 'ops', 'admin', 'super_admin', 'director_of_sales'], onboardId: 'estimates-tab' },
  { id: 'tasks', label: 'Tasks', icon: CheckCircle2, roles: ['all'], onboardId: null },
  { id: 'files', label: 'Files', icon: FileText, roles: ['all'], onboardId: 'files-tab' },
  { id: 'photos', label: 'Photos', icon: Image, roles: ['all'], onboardId: null },
  { id: 'work-orders', label: 'Work Orders', icon: Wrench, roles: ['ops', 'admin', 'super_admin', 'project_manager', 'operations_manager'], onboardId: null },
  { id: 'materials', label: 'Materials', icon: Package, roles: ['ops', 'admin', 'super_admin'], onboardId: null },
  { id: 'financials', label: 'Financials', icon: CreditCard, roles: ['ops', 'admin', 'super_admin', 'director_of_sales'], onboardId: null },
];

// Lead Tabs Configuration (simpler set)
const LEAD_TABS = [
  { id: 'overview', label: 'Overview', icon: FileText, roles: ['all'] },
  { id: 'details', label: 'Details', icon: ClipboardList, roles: ['all'] },
  { id: 'notes', label: 'Notes', icon: MessageSquare, roles: ['all'] },
  { id: 'activity', label: 'Activity', icon: History, roles: ['all'] },
];

// Lead Status Options
const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-500' },
  { value: 'contacted', label: 'Contacted', color: 'bg-cyan-500' },
  { value: 'qualified', label: 'Qualified', color: 'bg-blue-600' },
  { value: 'proposal', label: 'Proposal Sent', color: 'bg-orange-500' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-amber-500' },
  { value: 'converted', label: 'Converted', color: 'bg-green-500' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500' },
];

// Project Stages (Pre-Sale + Production)
const PROJECT_STAGES = [
  // Pre-sale stages
  { id: 'intro_call', name: 'Intro Call', number: 1, color: '#06b6d4', category: 'presale' },
  { id: 'appointment_set', name: 'Appt Set', number: 2, color: '#6366f1', category: 'presale' },
  { id: 'demo', name: 'Demo', number: 3, color: '#3b82f6', category: 'presale' },
  { id: 'pending_signature', name: 'Awaiting Approval', number: 4, color: '#f97316', category: 'presale' },
  { id: 'signed_agreement', name: 'Signed', number: 5, color: '#10b981', category: 'presale' },
  // Production stages
  { id: 'scheduled', name: 'Scheduled', number: 6, color: '#0ea5e9', category: 'production' },
  { id: 'prep', name: 'Prep', number: 7, color: '#0284c7', category: 'production' },
  { id: 'production', name: 'Production', number: 8, color: '#0369a1', category: 'production' },
  { id: 'installation', name: 'Installation', number: 9, color: '#14b8a6', category: 'production' },
  { id: 'final_inspection', name: 'Final Inspection', number: 10, color: '#22c55e', category: 'production' },
];

// Pre-sale only stages (for filtering)
const PRESALE_STAGES = ['intro_call', 'appointment_set', 'demo', 'pending_signature', 'signed_agreement'];
const PRODUCTION_STAGES = ['scheduled', 'prep', 'production', 'installation', 'final_inspection'];

// Priority Options
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-400' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500' },
];

// Source-based configuration
const SOURCE_CONFIG = {
  sales: {
    title: 'Leads & Projects',
    subtitle: 'Manage your sales pipeline',
    defaultMode: 'leads',
    showLeads: true,
    showProjects: true,
    projectFilter: PRESALE_STAGES, // Show pre-sale only in Sales Hub
    rightPanelTabs: ['overview', 'details', 'estimates', 'tasks', 'files'],
  },
  ops: {
    title: 'Active Jobs',
    subtitle: 'Manage operations and production',
    defaultMode: 'projects',
    showLeads: false,
    showProjects: true,
    projectFilter: [...PRESALE_STAGES, ...PRODUCTION_STAGES], // Show all stages in Ops
    rightPanelTabs: ['overview', 'details', 'scheduling', 'crew', 'tasks', 'files'],
  },
  callcenter: {
    title: 'Leads & Projects',
    subtitle: 'Call center workspace',
    defaultMode: 'leads',
    showLeads: true,
    showProjects: true,
    projectFilter: PRESALE_STAGES, // Show pre-sale only in Call Center
    rightPanelTabs: ['overview', 'contact', 'call_log', 'tasks'],
  },
};

export const UnifiedRecordsWorkspace = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  
  // Get source and initial mode from URL
  const source = searchParams.get('source') || 'sales';
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.sales;
  const initialMode = searchParams.get('mode') || config.defaultMode;
  const preselectedId = searchParams.get('id');
  // Check if coming from board - hide list sidebar when view=detail
  const isDetailView = searchParams.get('view') === 'detail';
  
  // Main state
  const [activeMode, setActiveMode] = useState(initialMode);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  // Auto-collapse sidebar when view=detail (coming from board)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isDetailView);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  
  // Reference data
  const [users, setUsers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Dialogs
  const [createLeadDialogOpen, setCreateLeadDialogOpen] = useState(false);
  const [convertToProjectDialogOpen, setConvertToProjectDialogOpen] = useState(false);
  const [editLeadDialogOpen, setEditLeadDialogOpen] = useState(false);
  const [editLeadData, setEditLeadData] = useState(null);
  const [editLeadLoading, setEditLeadLoading] = useState(false);
  
  // Work Order Modal state
  const [woModalOpen, setWoModalOpen] = useState(false);
  const [woRefreshKey, setWoRefreshKey] = useState(0);
  
  // Tab state with URL persistence
  const tabParam = searchParams.get('tab') || 'overview';
  const [activeDetailTab, setActiveDetailTab] = useState(tabParam);
  
  // Get visible tabs based on role and source
  const getVisibleTabs = useCallback((tabConfig) => {
    const userRole = user?.role || 'user';
    const effectiveSource = source;
    
    return tabConfig.filter(tab => {
      // 'all' means visible to everyone
      if (tab.roles.includes('all')) return true;
      
      // Check if user's role is in allowed roles
      if (tab.roles.includes(userRole)) return true;
      
      // Source-based visibility
      if (effectiveSource === 'ops' && tab.roles.includes('ops')) return true;
      if (effectiveSource === 'sales' && tab.roles.includes('sales')) return true;
      if (effectiveSource === 'callcenter') {
        // Call center has limited tab access
        return ['overview', 'details', 'tasks', 'files', 'photos'].includes(tab.id);
      }
      
      return false;
    });
  }, [user?.role, source]);
  
  // Update URL when tab changes
  const handleTabChange = useCallback((newTab) => {
    setActiveDetailTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  // Validate tab on mount/navigation - fall back to overview if invalid
  useEffect(() => {
    const validTabs = activeMode === 'projects' 
      ? getVisibleTabs(PROJECT_TABS).map(t => t.id)
      : getVisibleTabs(LEAD_TABS).map(t => t.id);
    
    if (!validTabs.includes(activeDetailTab)) {
      setActiveDetailTab('overview');
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'overview');
      setSearchParams(newParams, { replace: true });
    }
  }, [activeMode, activeDetailTab, getVisibleTabs, searchParams, setSearchParams]);
  
  // Form data
  const [leadFormData, setLeadFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'FL',
    zip_code: '',
    source: 'phone',
    status: 'new',
    priority: 'medium',
    estimated_value: '',
    notes: '',
    assigned_to: '',
    market: ''
  });

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Sync URL when mode changes from external navigation (not toggle clicks)
  // Toggle clicks handle their own URL updates
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    if (urlMode && urlMode !== activeMode) {
      setActiveMode(urlMode);
    }
  }, [searchParams]);

  // Handle preselected record from URL - with auto-correction and fallback fetch
  useEffect(() => {
    const handlePreselection = async () => {
      if (!preselectedId || loading) return;

      // First try to find in current mode's list
      if (activeMode === 'leads') {
        const lead = leads.find(l => l.id === preselectedId);
        if (lead) {
          setSelectedLead(lead);
          return;
        }
        
        // Check if it's actually a project (mode auto-correction)
        const projectMatch = projects.find(p => p.id === preselectedId);
        if (projectMatch) {
          setActiveMode('projects');
          setSelectedProject(projectMatch);
          // Update URL to reflect correct mode
          const newParams = new URLSearchParams(searchParams);
          newParams.set('mode', 'projects');
          setSearchParams(newParams, { replace: true });
          return;
        }
      } else {
        const project = projects.find(p => p.id === preselectedId);
        if (project) {
          setSelectedProject(project);
          return;
        }
        
        // Check if it's actually a lead (mode auto-correction)
        const leadMatch = leads.find(l => l.id === preselectedId);
        if (leadMatch) {
          setActiveMode('leads');
          setSelectedLead(leadMatch);
          // Update URL to reflect correct mode
          const newParams = new URLSearchParams(searchParams);
          newParams.set('mode', 'leads');
          setSearchParams(newParams, { replace: true });
          return;
        }
      }

      // Record not in either list - try to fetch it directly
      try {
        // Try fetching as a lead first
        const leadRes = await axios.get(`${API}/leads/${preselectedId}`).catch(() => null);
        if (leadRes?.data) {
          // Inject into leads list and select it
          setLeads(prev => {
            if (!prev.find(l => l.id === preselectedId)) {
              return [leadRes.data, ...prev];
            }
            return prev;
          });
          setSelectedLead(leadRes.data);
          if (activeMode !== 'leads') {
            setActiveMode('leads');
            const newParams = new URLSearchParams(searchParams);
            newParams.set('mode', 'leads');
            setSearchParams(newParams, { replace: true });
          }
          return;
        }

        // Try fetching as a job/project
        const jobRes = await axios.get(`${API}/jobs/${preselectedId}`).catch(() => null);
        if (jobRes?.data) {
          // Inject into projects list and select it
          setProjects(prev => {
            if (!prev.find(p => p.id === preselectedId)) {
              return [jobRes.data, ...prev];
            }
            return prev;
          });
          setSelectedProject(jobRes.data);
          if (activeMode !== 'projects') {
            setActiveMode('projects');
            const newParams = new URLSearchParams(searchParams);
            newParams.set('mode', 'projects');
            setSearchParams(newParams, { replace: true });
          }
          return;
        }

        // Record truly not found
        console.warn(`Record ${preselectedId} not found in leads or jobs`);
      } catch (err) {
        console.error('Failed to fetch preselected record:', err);
      }
    };

    handlePreselection();
  }, [preselectedId, leads, projects, loading, activeMode, searchParams, setSearchParams]);

  const fetchData = async () => {
    try {
      const [leadsRes, jobsRes, usersRes, marketsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/users`),
        axios.get(`${API}/markets`),
        axios.get(`${API}/teams`)
      ]);
      
      setLeads(leadsRes.data);
      
      // Filter jobs based on source configuration
      const filteredProjects = jobsRes.data.filter(job => 
        config.projectFilter.includes(job.stage)
      );
      setProjects(filteredProjects);
      
      setUsers(usersRes.data);
      setMarkets(marketsRes.data);
      setTeams(teamsRes.data);
      
      // Auto-select first item if none selected
      if (!selectedLead && leadsRes.data.length > 0) {
        setSelectedLead(leadsRes.data[0]);
      }
      if (!selectedProject && filteredProjects.length > 0) {
        setSelectedProject(filteredProjects[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchQuery === '' || 
      `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.address?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesMarket = filterMarket === 'all' || lead.market === filterMarket;
    const matchesAssignedTo = filterAssignedTo === 'all' || lead.assigned_to === filterAssignedTo;
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || lead.priority === filterPriority;
    
    // Exclude converted and lost leads from the active list
    const isActive = lead.status !== 'converted' && lead.status !== 'lost';
    
    return matchesSearch && matchesMarket && matchesAssignedTo && matchesStatus && matchesPriority && isActive;
  });

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = searchQuery === '' ||
      project.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.customer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.customer_phone?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesMarket = filterMarket === 'all' || project.market_id === filterMarket;
    const matchesAssignedTo = filterAssignedTo === 'all' || project.assigned_to === filterAssignedTo;
    const matchesStage = filterStage === 'all' || project.stage === filterStage;
    const matchesPriority = filterPriority === 'all' || project.priority === filterPriority;
    
    return matchesSearch && matchesMarket && matchesAssignedTo && matchesStage && matchesPriority;
  });

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      const data = { ...leadFormData };
      if (data.estimated_value) data.estimated_value = parseFloat(data.estimated_value);
      
      const response = await axios.post(`${API}/leads`, data);
      toast.success('Lead created successfully');
      setCreateLeadDialogOpen(false);
      resetLeadForm();
      fetchData();
      
      // Select the newly created lead
      setSelectedLead(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const resetLeadForm = () => {
    setLeadFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'FL',
      zip_code: '',
      source: 'phone',
      status: 'new',
      priority: 'medium',
      estimated_value: '',
      notes: '',
      assigned_to: '',
      market: ''
    });
  };

  const handleConvertToProject = async () => {
    if (!selectedLead) return;
    
    try {
      // Create a job/project from the lead
      const projectData = {
        customer_name: `${selectedLead.first_name} ${selectedLead.last_name}`,
        customer_email: selectedLead.email,
        customer_phone: selectedLead.phone,
        address: selectedLead.address,
        city: selectedLead.city,
        state: selectedLead.state,
        zip_code: selectedLead.zip_code,
        stage: 'intro_call', // Start at intro_call stage
        estimated_value: selectedLead.estimated_value,
        assigned_to: selectedLead.assigned_to,
        market_id: selectedLead.market,
        notes: selectedLead.notes,
        lead_id: selectedLead.id // Link back to original lead
      };
      
      const response = await axios.post(`${API}/jobs`, projectData);
      
      // Update lead status to converted
      await axios.patch(`${API}/leads/${selectedLead.id}`, { status: 'converted' });
      
      toast.success('Lead converted to Project successfully!');
      setConvertToProjectDialogOpen(false);
      
      // Switch to Projects tab and select the new project
      setActiveMode('projects');
      fetchData();
      
      // Select the newly created project
      setTimeout(() => {
        setSelectedProject(response.data);
      }, 500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  // Open edit lead dialog
  const openEditLeadDialog = (lead) => {
    setEditLeadData({
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      city: lead.city || '',
      state: lead.state || '',
      zip_code: lead.zip_code || '',
      source: lead.source || '',
      status: lead.status || 'new',
      priority: lead.priority || 'medium',
      estimated_value: lead.estimated_value || '',
      notes: lead.notes || '',
      assigned_to: lead.assigned_to || '',
      market: lead.market || ''
    });
    setEditLeadDialogOpen(true);
  };

  // Handle edit lead submit
  const handleEditLeadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLead?.id) return;
    
    setEditLeadLoading(true);
    try {
      const response = await axios.patch(
        `${API}/leads/${selectedLead.id}`,
        editLeadData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the local state with the updated lead
      setLeads(prev => prev.map(l => l.id === selectedLead.id ? response.data : l));
      setSelectedLead(response.data);
      
      toast.success('Lead updated successfully');
      setEditLeadDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setEditLeadLoading(false);
    }
  };

  // Helper functions
  const formatCurrency = (amount) => {
    return amount ? `$${Number(amount).toLocaleString()}` : '$0';
  };

  const getStatusBadge = (status) => {
    const statusObj = LEAD_STATUSES.find(s => s.value === status);
    return statusObj || { label: status, color: 'bg-slate-400' };
  };

  const getPriorityBadge = (priority) => {
    const priorityObj = PRIORITIES.find(p => p.value === priority);
    return priorityObj || { label: priority, color: 'bg-slate-400' };
  };

  const getProjectStageInfo = (stage) => {
    return PROJECT_STAGES.find(s => s.id === stage) || { name: stage, number: 0, color: '#6b7280' };
  };

  const getCurrentStageIndex = (stage) => {
    return PROJECT_STAGES.findIndex(s => s.id === stage);
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? `${foundUser.first_name} ${foundUser.last_name}` : 'Unassigned';
  };

  const getMarketName = (marketId) => {
    const market = markets.find(m => m.id === marketId);
    return market ? market.name : 'No Market';
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unassigned';
  };

  // Get stages for filter based on source
  const getAvailableStages = () => {
    return PROJECT_STAGES.filter(s => config.projectFilter.includes(s.id));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex h-[calc(100vh-4rem)] bg-slate-100 dark:bg-slate-950">
        {/* Left Panel - List */}
        <div className={`${sidebarCollapsed ? 'w-0' : 'w-96'} border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-900 transition-all duration-300 overflow-hidden`}>
          {/* Header with Tabs */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">{config.title}</h2>
                <p className="text-xs text-slate-500">{config.subtitle}</p>
              </div>
              <Button 
                size="sm" 
                className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/50"
                onClick={() => activeMode === 'leads' ? setCreateLeadDialogOpen(true) : navigate('/jobs/create')}
                data-onboard={activeMode === 'leads' ? 'new-lead-btn' : 'new-job-btn'}
              >
                <Plus className="h-4 w-4 mr-1" />
                {activeMode === 'leads' ? 'New Lead' : 'New Project'}
              </Button>
            </div>
            
            {/* Tab Toggle - Only show if source allows both */}
            {config.showLeads && config.showProjects && (
              <Tabs value={activeMode} onValueChange={(v) => {
                setActiveMode(v);
                // Clear selections when switching modes
                if (v === 'leads') {
                  setSelectedProject(null);
                } else {
                  setSelectedLead(null);
                }
                // Reset filters
                setSearchQuery('');
                setFilterMarket('all');
                setFilterAssignedTo('all');
                setFilterStatus('all');
                setFilterStage('all');
                setFilterPriority('all');
                // Update URL immediately
                const newParams = new URLSearchParams(searchParams);
                newParams.set('mode', v);
                newParams.delete('id'); // Clear any preselected ID
                newParams.set('tab', 'overview');
                setSearchParams(newParams, { replace: true });
              }}>
                <TabsList className="w-full grid grid-cols-2">
                  <TabsTrigger value="leads" className="flex items-center gap-2" data-testid="toggle-leads">
                    <Target className="h-4 w-4" />
                    Leads
                    <Badge variant="secondary" className="ml-1 text-xs">{filteredLeads.length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="projects" className="flex items-center gap-2" data-testid="toggle-projects">
                    <Briefcase className="h-4 w-4" />
                    Projects
                    <Badge variant="secondary" className="ml-1 text-xs">{filteredProjects.length}</Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={activeMode === 'leads' ? "Search leads..." : "Search projects..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              />
            </div>

            {/* Filters Row */}
            <div className="flex gap-2 flex-wrap">
              <Select value={filterMarket} onValueChange={setFilterMarket}>
                <SelectTrigger className="flex-1 h-8 text-xs min-w-[100px]">
                  <SelectValue placeholder="Market" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Markets</SelectItem>
                  {markets.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
                <SelectTrigger className="flex-1 h-8 text-xs min-w-[100px]">
                  <SelectValue placeholder="Assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {users.filter(u => ['sales', 'sales_rep', 'sales_manager', 'director_of_sales'].includes(u.role)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeMode === 'leads' ? (
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="flex-1 h-8 text-xs min-w-[100px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {LEAD_STATUSES.filter(s => s.value !== 'converted' && s.value !== 'lost').map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={filterStage} onValueChange={setFilterStage}>
                  <SelectTrigger className="flex-1 h-8 text-xs min-w-[100px]">
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    {getAvailableStages().map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Count */}
            <div className="text-xs text-slate-500">
              {activeMode === 'leads' 
                ? `${filteredLeads.length} lead${filteredLeads.length !== 1 ? 's' : ''}`
                : `${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''}`
              }
            </div>
          </div>

          {/* Scrollable List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {activeMode === 'leads' ? (
              // Leads List
              filteredLeads.map((lead) => (
                <div
                  key={lead.id}
                  onClick={() => setSelectedLead(lead)}
                  className={`
                    p-4 rounded-lg cursor-pointer transition-all duration-200
                    hover:shadow-lg border
                    ${selectedLead?.id === lead.id 
                      ? 'bg-gradient-to-r from-blue-500/20 to-sky-400/20 border-blue-400 shadow-lg shadow-blue-400/30' 
                      : 'bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border-slate-200 dark:border-slate-700'
                    }
                  `}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base text-slate-900 dark:text-white">
                          {lead.first_name} {lead.last_name}
                        </h3>
                        <p className="text-sm text-slate-400">
                          {lead.address || 'No address'}
                        </p>
                      </div>
                      <Badge className={`${getPriorityBadge(lead.priority).color} text-white text-xs`}>
                        {getPriorityBadge(lead.priority).label}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        LEAD-{lead.id?.slice(0, 6).toUpperCase()}
                      </span>
                      <Badge className={`${getStatusBadge(lead.status).color} text-white text-xs`}>
                        {getStatusBadge(lead.status).label}
                      </Badge>
                    </div>

                    {lead.estimated_value && (
                      <div className="text-sm font-bold text-emerald-500">
                        {formatCurrency(lead.estimated_value)}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              // Projects List
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className={`
                    p-4 rounded-lg cursor-pointer transition-all duration-200
                    hover:shadow-lg border
                    ${selectedProject?.id === project.id 
                      ? 'bg-gradient-to-r from-blue-500/15 to-sky-400/15 border-blue-500 shadow-lg shadow-blue-500/25 ring-1 ring-blue-400/50' 
                      : 'bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-300'
                    }
                  `}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base text-slate-900 dark:text-white">
                          {project.address || 'No Address'}
                        </h3>
                        <p className="text-sm text-slate-400">
                          {project.customer_name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">
                        PRJ-{project.id?.slice(0, 6).toUpperCase()}
                      </span>
                      <Badge 
                        className="text-white text-xs"
                        style={{ backgroundColor: getProjectStageInfo(project.stage).color }}
                      >
                        {getProjectStageInfo(project.stage).name}
                      </Badge>
                    </div>

                    {project.estimated_value && (
                      <div className="text-sm font-bold text-emerald-500">
                        {formatCurrency(project.estimated_value)}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {((activeMode === 'leads' && filteredLeads.length === 0) || 
              (activeMode === 'projects' && filteredProjects.length === 0)) && (
              <div className="text-center py-12 text-slate-400">
                No {activeMode === 'leads' ? 'leads' : 'projects'} found
              </div>
            )}
          </div>
        </div>

        {/* Collapse/Expand Button */}
        <div className="relative">
          <Button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute top-4 -left-3 z-50 w-6 h-12 p-0 rounded-r-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 shadow-lg"
            size="sm"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4 text-white" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-white" />
            )}
          </Button>
        </div>

        {/* Right Panel - Detail View */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {activeMode === 'leads' ? (
            // Lead Detail View
            selectedLead ? (
              <LeadDetailView 
                lead={selectedLead}
                source={source}
                users={users}
                markets={markets}
                getUserName={getUserName}
                getMarketName={getMarketName}
                getStatusBadge={getStatusBadge}
                getPriorityBadge={getPriorityBadge}
                formatCurrency={formatCurrency}
                onConvertToProject={() => setConvertToProjectDialogOpen(true)}
                onEditLead={openEditLeadDialog}
                onRefresh={fetchData}
                config={config}
              />
            ) : (
              <EmptyDetailView type="lead" />
            )
          ) : (
            // Project Detail View
            selectedProject ? (
              <ProjectDetailView 
                project={selectedProject}
                source={source}
                user={user}
                users={users}
                markets={markets}
                teams={teams}
                getUserName={getUserName}
                getMarketName={getMarketName}
                getTeamName={getTeamName}
                getProjectStageInfo={getProjectStageInfo}
                getCurrentStageIndex={getCurrentStageIndex}
                formatCurrency={formatCurrency}
                PROJECT_STAGES={PROJECT_STAGES}
                activeDetailTab={activeDetailTab}
                setActiveDetailTab={setActiveDetailTab}
                onRefresh={fetchData}
                navigate={navigate}
                config={config}
                woModalOpen={woModalOpen}
                setWoModalOpen={setWoModalOpen}
                woRefreshKey={woRefreshKey}
                setWoRefreshKey={setWoRefreshKey}
              />
            ) : (
              <EmptyDetailView type="project" />
            )
          )}
        </div>
      </div>

      {/* Create Lead Dialog */}
      <Dialog open={createLeadDialogOpen} onOpenChange={setCreateLeadDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
            <DialogDescription>Add a new lead to your pipeline</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={leadFormData.first_name}
                  onChange={(e) => setLeadFormData({...leadFormData, first_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={leadFormData.last_name}
                  onChange={(e) => setLeadFormData({...leadFormData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={leadFormData.email}
                  onChange={(e) => setLeadFormData({...leadFormData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={leadFormData.phone}
                  onChange={(e) => setLeadFormData({...leadFormData, phone: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={leadFormData.address}
                onChange={(e) => setLeadFormData({...leadFormData, address: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={leadFormData.city}
                  onChange={(e) => setLeadFormData({...leadFormData, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={leadFormData.state}
                  onChange={(e) => setLeadFormData({...leadFormData, state: e.target.value})}
                />
              </div>
              <div>
                <Label>Zip</Label>
                <Input
                  value={leadFormData.zip_code}
                  onChange={(e) => setLeadFormData({...leadFormData, zip_code: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={leadFormData.status} onValueChange={(v) => setLeadFormData({...leadFormData, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={leadFormData.priority} onValueChange={(v) => setLeadFormData({...leadFormData, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Value</Label>
                <Input
                  type="number"
                  value={leadFormData.estimated_value}
                  onChange={(e) => setLeadFormData({...leadFormData, estimated_value: e.target.value})}
                  placeholder="$0"
                />
              </div>
              <div>
                <Label>Market</Label>
                <Select value={leadFormData.market} onValueChange={(v) => setLeadFormData({...leadFormData, market: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select market" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assigned To</Label>
              <Select value={leadFormData.assigned_to} onValueChange={(v) => setLeadFormData({...leadFormData, assigned_to: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select rep" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => ['sales', 'sales_rep', 'sales_manager', 'director_of_sales'].includes(u.role)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={leadFormData.notes}
                onChange={(e) => setLeadFormData({...leadFormData, notes: e.target.value})}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateLeadDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Create Lead</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert to Project Dialog */}
      <Dialog open={convertToProjectDialogOpen} onOpenChange={setConvertToProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Lead to Project</DialogTitle>
            <DialogDescription>
              This will create a new Project from this lead and mark the lead as converted.
            </DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-2">
                <p><strong>Name:</strong> {selectedLead.first_name} {selectedLead.last_name}</p>
                <p><strong>Address:</strong> {selectedLead.address || 'No address'}</p>
                <p><strong>Phone:</strong> {selectedLead.phone || 'No phone'}</p>
                <p><strong>Email:</strong> {selectedLead.email || 'No email'}</p>
                {selectedLead.estimated_value && (
                  <p><strong>Est. Value:</strong> {formatCurrency(selectedLead.estimated_value)}</p>
                )}
              </div>
              <p className="text-sm text-slate-500">
                The new Project will start at the &quot;Intro Call&quot; stage.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertToProjectDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToProject} className="bg-green-500 hover:bg-green-600">
              <ArrowUpRight className="h-4 w-4 mr-2" />
              Convert to Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editLeadDialogOpen} onOpenChange={setEditLeadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
            <DialogDescription>
              Update lead information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditLeadSubmit} className="space-y-4">
            {/* Contact Information */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground">Contact Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit_first_name">First Name</Label>
                  <Input
                    id="edit_first_name"
                    value={editLeadData?.first_name || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_last_name">Last Name</Label>
                  <Input
                    id="edit_last_name"
                    value={editLeadData?.last_name || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, last_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editLeadData?.email || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_phone">Phone</Label>
                  <Input
                    id="edit_phone"
                    value={editLeadData?.phone || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, phone: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit_address">Address</Label>
                <Input
                  id="edit_address"
                  value={editLeadData?.address || ''}
                  onChange={(e) => setEditLeadData({...editLeadData, address: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="edit_city">City</Label>
                  <Input
                    id="edit_city"
                    value={editLeadData?.city || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_state">State</Label>
                  <Input
                    id="edit_state"
                    value={editLeadData?.state || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, state: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_zip">Zip Code</Label>
                  <Input
                    id="edit_zip"
                    value={editLeadData?.zip_code || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, zip_code: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Lead Details */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground">Lead Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit_status">Status</Label>
                  <Select 
                    value={editLeadData?.status || 'new'} 
                    onValueChange={(value) => setEditLeadData({...editLeadData, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit_priority">Priority</Label>
                  <Select 
                    value={editLeadData?.priority || 'medium'} 
                    onValueChange={(value) => setEditLeadData({...editLeadData, priority: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(priority => (
                        <SelectItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit_estimated_value">Estimated Value</Label>
                  <Input
                    id="edit_estimated_value"
                    type="number"
                    value={editLeadData?.estimated_value || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, estimated_value: e.target.value ? parseFloat(e.target.value) : ''})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_source">Source</Label>
                  <Input
                    id="edit_source"
                    value={editLeadData?.source || ''}
                    onChange={(e) => setEditLeadData({...editLeadData, source: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit_market">Market</Label>
                  <Select 
                    value={editLeadData?.market || ''} 
                    onValueChange={(value) => setEditLeadData({...editLeadData, market: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select market" />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map(market => (
                        <SelectItem key={market.id} value={market.id}>
                          {market.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit_assigned_to">Assigned To</Label>
                  <Select 
                    value={editLeadData?.assigned_to || ''} 
                    onValueChange={(value) => setEditLeadData({...editLeadData, assigned_to: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="edit_notes">Notes</Label>
                <Textarea
                  id="edit_notes"
                  value={editLeadData?.notes || ''}
                  onChange={(e) => setEditLeadData({...editLeadData, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditLeadDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLeadLoading}>
                {editLeadLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Work Order Create Modal (for quick create from project header) */}
      {selectedProject && (
        <CreateWorkOrderModal
          isOpen={woModalOpen}
          onClose={() => setWoModalOpen(false)}
          onSuccess={() => {
            setWoRefreshKey(prev => prev + 1);
            // If on Work Orders tab, the refresh key will trigger re-fetch
          }}
          prefillData={{
            job_id: selectedProject.id,
            job_name: selectedProject.address || selectedProject.customer_name,
            customer_name: selectedProject.customer_name,
            address: selectedProject.address,
            market_id: selectedProject.market
          }}
        />
      )}
    </Layout>
  );
};

// Lead Detail View Component
const LeadDetailView = ({ 
  lead, source, users, markets, getUserName, getMarketName, 
  getStatusBadge, getPriorityBadge, formatCurrency,
  onConvertToProject, onEditLead, onRefresh, config
}) => {
  return (
    <>
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {lead.first_name} {lead.last_name}
              </h1>
              <Badge className={`${getStatusBadge(lead.status).color} text-white`}>
                {getStatusBadge(lead.status).label}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-slate-400">
              <span>{lead.address || 'No address'}</span>
              <span>•</span>
              <span>LEAD-{lead.id?.slice(0, 6).toUpperCase()}</span>
            </div>
          </div>
          {lead.status !== 'converted' && lead.status !== 'lost' && (
            <Button 
              onClick={onConvertToProject}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg"
            >
              <ArrowUpRight className="h-4 w-4 mr-2" />
              Convert to Project
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-2 gap-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-500" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-slate-400" />
                <span>{lead.phone || 'No phone'}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-slate-400" />
                <span>{lead.email || 'No email'}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span>
                  {lead.address ? `${lead.address}, ${lead.city || ''} ${lead.state || ''} ${lead.zip_code || ''}` : 'No address'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Lead Details */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Lead Details
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onEditLead(lead)}
                  data-testid="edit-lead-btn"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Priority</span>
                <Badge className={`${getPriorityBadge(lead.priority).color} text-white`}>
                  {getPriorityBadge(lead.priority).label}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Est. Value</span>
                <span className="font-bold text-emerald-500">{formatCurrency(lead.estimated_value)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Market</span>
                <span>{getMarketName(lead.market)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Assigned To</span>
                <span>{getUserName(lead.assigned_to)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Source</span>
                <Badge variant="outline">{lead.source || 'Unknown'}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Call Center - Call Log Section */}
          {source === 'callcenter' && (
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PhoneCall className="h-5 w-5 text-green-500" />
                  Call Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-400">
                  <Phone className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No call activity recorded yet</p>
                  <Button className="mt-4" variant="outline" size="sm">
                    <PhoneCall className="h-4 w-4 mr-2" />
                    Log a Call
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {lead.notes && (
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-500" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{lead.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3 flex-wrap">
                {lead.phone && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={`tel:${lead.phone}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      Call
                    </a>
                  </Button>
                )}
                {lead.email && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={`mailto:${lead.email}`}>
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </a>
                  </Button>
                )}
                <Button variant="outline" size="sm">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  SMS
                </Button>
                <Button variant="outline" size="sm">
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule
                </Button>
                {source === 'callcenter' && (
                  <Button variant="outline" size="sm">
                    <History className="h-4 w-4 mr-2" />
                    Call History
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

// Project Detail View Component
const ProjectDetailView = ({ 
  project, source, user, users, markets, teams, getUserName, getMarketName, getTeamName,
  getProjectStageInfo, getCurrentStageIndex, formatCurrency,
  PROJECT_STAGES, activeDetailTab, setActiveDetailTab, onRefresh, navigate, config,
  woModalOpen, setWoModalOpen, woRefreshKey, setWoRefreshKey
}) => {
  const currentStageIndex = getCurrentStageIndex(project.stage);
  const visibleStages = PROJECT_STAGES.filter(s => config.projectFilter.includes(s.id));
  
  // Check if user can create work orders
  const canCreateWO = WO_CREATE_ROLES.includes(user?.role?.toLowerCase());

  return (
    <>
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {project.address || 'Untitled Project'}
              </h1>
              <Badge 
                className="text-white"
                style={{ backgroundColor: getProjectStageInfo(project.stage).color }}
              >
                {getProjectStageInfo(project.stage).name}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-slate-400">
              <span>{project.customer_name}</span>
              <span>•</span>
              <span>PRJ-{project.id?.slice(0, 6).toUpperCase()}</span>
            </div>
          </div>
          
          {/* Quick Create Work Order Button */}
          {canCreateWO && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWoModalOpen(true)}
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400"
              data-testid="quick-create-wo-btn"
            >
              <Wrench className="h-4 w-4 mr-1.5" />
              + Work Order
            </Button>
          )}
        </div>
      </div>

      {/* Tabs - Dynamic based on PROJECT_TABS config */}
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
        <Tabs value={activeDetailTab} onValueChange={(tab) => {
          setActiveDetailTab(tab);
          // Update URL with tab param
          const newParams = new URLSearchParams(window.location.search);
          newParams.set('tab', tab);
          window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
        }}>
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0 flex-nowrap whitespace-nowrap">
            {PROJECT_TABS.map(tab => {
              // Role-based visibility
              const userRole = user?.role || 'user';
              const isVisible = tab.roles.includes('all') || 
                                tab.roles.includes(userRole) ||
                                (source === 'ops' && tab.roles.includes('ops')) ||
                                (source === 'sales' && tab.roles.includes('sales')) ||
                                (source === 'callcenter' && ['overview', 'details', 'tasks', 'files', 'photos'].includes(tab.id));
              
              if (!isVisible) return null;
              
              const TabIcon = tab.icon;
              return (
                <TabsTrigger 
                  key={tab.id}
                  value={tab.id} 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent flex items-center gap-1.5 px-3 py-2"
                  data-onboard={tab.onboardId || undefined}
                >
                  <TabIcon className="h-3.5 w-3.5" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeDetailTab === 'overview' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-500" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-slate-400" />
                  <span>{project.customer_name || 'No name'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <span>{project.customer_phone || 'No phone'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span>{project.customer_email || 'No email'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span>
                    {project.address ? `${project.address}, ${project.city || ''} ${project.state || ''} ${project.zip_code || ''}` : 'No address'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Project Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-500" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Est. Value</span>
                  <span className="font-bold text-emerald-500">{formatCurrency(project.estimated_value)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Market</span>
                  <span>{getMarketName(project.market_id)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Assigned To</span>
                  <span>{getUserName(project.assigned_to)}</span>
                </div>
                {source === 'ops' && project.assigned_team && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Crew</span>
                    <span>{getTeamName(project.assigned_team)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Created</span>
                  <span>{new Date(project.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 flex-wrap">
                  {project.customer_phone && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`tel:${project.customer_phone}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        Call
                      </a>
                    </Button>
                  )}
                  {project.customer_email && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`mailto:${project.customer_email}`}>
                        <Mail className="h-4 w-4 mr-2" />
                        Email
                      </a>
                    </Button>
                  )}
                  <Button variant="outline" size="sm">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    SMS
                  </Button>
                  {source === 'sales' && (
                    <Button variant="outline" size="sm">
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule Demo
                    </Button>
                  )}
                  {source === 'ops' && (
                    <>
                      <Button variant="outline" size="sm">
                        <Truck className="h-4 w-4 mr-2" />
                        Assign Crew
                      </Button>
                      <Button variant="outline" size="sm">
                        <Calendar className="h-4 w-4 mr-2" />
                        Schedule Install
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeDetailTab === 'details' && (
          <ProjectDetailsTab
            project={project}
            user={user}
            users={users}
            markets={markets}
            source={source}
            onRefresh={onRefresh}
          />
        )}

        {activeDetailTab === 'communications' && (
          <JobCommunicationsTab job={project} />
        )}

        {activeDetailTab === 'scope' && (
          <ScopeOfWorkTab
            project={project}
            user={user}
            onRefresh={onRefresh}
          />
        )}

        {activeDetailTab === 'estimates' && (
          <EstimatesTab
            jobId={project.id}
            project={project}
            user={user}
            onRefresh={onRefresh}
          />
        )}

        {activeDetailTab === 'scheduling' && (
          <Card>
            <CardContent className="p-6">
              <p className="text-slate-500">Scheduling information will be displayed here.</p>
            </CardContent>
          </Card>
        )}

        {activeDetailTab === 'crew' && (
          <Card>
            <CardContent className="p-6">
              <p className="text-slate-500">Crew assignment information will be displayed here.</p>
            </CardContent>
          </Card>
        )}

        {activeDetailTab === 'tasks' && (
          <Card>
            <CardContent className="p-6">
              <p className="text-slate-500">Tasks related to this project will be displayed here.</p>
            </CardContent>
          </Card>
        )}

        {activeDetailTab === 'files' && (
          <FilesTab 
            jobId={project?.id}
            currentUserId={user?.id}
          />
        )}

        {activeDetailTab === 'measurements' && (
          <MeasurementsTab
            project={project}
            user={user}
            onRefresh={() => {}}
          />
        )}

        {activeDetailTab === 'photos' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Image className="h-5 w-5 text-blue-500" />
                  <h3 className="font-semibold">Photos</h3>
                </div>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Photo
                </Button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="aspect-square bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-600">
                  <Image className="h-8 w-8 text-slate-400" />
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4">No photos uploaded yet. Add photos to document the project progress.</p>
            </CardContent>
          </Card>
        )}

        {activeDetailTab === 'materials' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-500" />
                  <h3 className="font-semibold">Materials</h3>
                </div>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Material
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Material</th>
                      <th className="px-4 py-2 text-left font-medium">Quantity</th>
                      <th className="px-4 py-2 text-left font-medium">Unit Cost</th>
                      <th className="px-4 py-2 text-left font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-8 text-center text-slate-500 italic" colSpan={4}>
                        No materials added yet
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeDetailTab === 'work-orders' && (
          <WorkOrdersTab 
            project={project} 
            onRefresh={onRefresh}
            refreshKey={woRefreshKey}
          />
        )}

        {activeDetailTab === 'financials' && (
          <AccountingSection
            project={project}
            user={user}
            onRefresh={onRefresh}
          />
        )}
      </div>
    </>
  );
};

// Empty Detail View Component
const EmptyDetailView = ({ type }) => (
  <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-900/50">
    <div className="text-center">
      {type === 'lead' ? (
        <>
          <Target className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-400 mb-2">No Lead Selected</h3>
          <p className="text-slate-400">Select a lead from the list to view details</p>
        </>
      ) : (
        <>
          <Briefcase className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-400 mb-2">No Project Selected</h3>
          <p className="text-slate-400">Select a project from the list to view details</p>
        </>
      )}
    </div>
  </div>
);

export default UnifiedRecordsWorkspace;
